import { Path } from '@softline/core';
import { isSignal, Signal } from '@angular/core';
import { FilterOperator } from '../../types/filter-operators';
import { PageFilterFactoryRef } from './page-filter.function';
import { PageFilterSource } from '../types/page-filter-source';
import { PageFilterItem, PageFilterToggleValue } from '../types/page-filter-item';
import { validateToggleValue } from './page-filter-toggle.function';

export interface PageFilterItemOptions<T extends object, TValue> {
  source?: PageFilterSource<T>;
  field: Path<T>;
  operator: FilterOperator;
  visible?: Signal<boolean>;
  order?: number;
  groupKey?: string;
  /** always show this item in the "Filters" menu bar, even when unset */
  menuBar?: boolean;
}

export interface PageFilterToggleItemOptions<T extends object, TValue>
  extends PageFilterItemOptions<T, TValue> {
  /**
   * The fixed value the toggle writes; must match the field's datatype and the
   * operator's shape (in/notIn: array, between: range, operator-only filters
   * like boolean true/false or empty/notEmpty: no value). Signals resolve at
   * toggle time.
   */
  value?: PageFilterToggleValue<TValue> | Signal<PageFilterToggleValue<TValue>>;
}

export function pageFilterItem<T extends object, TValue>(ref: PageFilterFactoryRef, options: PageFilterItemOptions<T, TValue >): PageFilterItem<T, TValue> {
  const item = createPageFilterItem<T, TValue>(ref, options, 'pageFilterItem');
  ref.items.push(item);
  return item;
}

/**
 * Like pageFilterItem, but rendered as an on/off switch that writes a fixed
 * operator+value filter when switched on and removes it when switched off.
 * There is no detail view for toggle items.
 */
export function pageFilterToggleItem<T extends object, TValue>(
  ref: PageFilterFactoryRef,
  options: PageFilterToggleItemOptions<T, TValue>
): PageFilterItem<T, TValue> {
  const item = createPageFilterItem<T, TValue>(ref, options, 'pageFilterToggleItem');
  // static values are validated up front; signal values re-validate at toggle time
  if (!isSignal(options.value))
    validateToggleValue(item.config, item.operator, options.value, 'pageFilterToggleItem');
  item.toggle = { value: options.value };
  ref.items.push(item);
  return item;
}

export function createPageFilterItem<T extends object, TValue>(
  ref: PageFilterFactoryRef,
  options: PageFilterItemOptions<T, TValue>,
  context: string
): PageFilterItem<T, TValue> {
  if(!options.source && ref.sources.length === 0)
    throw new Error(`[${context}] Please specify a source first or add a source to the filter`);
  const source = options.source ?? ref.sources[0];
  const config = source.state.config.items.find(o => o.field === options.field);
  if(!config)
    throw new Error(`[${context}] FilterConfig for ${options.field} not found`);
  if((config.allowedOperators as FilterOperator[]).indexOf(options.operator) === -1)
    throw new Error(`[${context}] Operator ${options.operator} not allowed for field ${options.field}`);
  return {
    field: options.field,
    operator: options.operator,
    visible: options.visible,
    order: options.order ?? Number.POSITIVE_INFINITY,
    groupKey: options.groupKey,
    menuBar: options.menuBar,
    source,
    config,
  };
}
